import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FexidocLogo } from "@/components/FexidocLogo";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const industries = ["Mining","Engineering","Construction","Manufacturing","Professional Services","Energy","Maintenance","Safety","Other"];

export default function Onboarding() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [ws, setWs] = useState({ name:"", industry:"Engineering", company_name:"", primary_colour:"#1478FF", secondary_colour:"#725BFF" });
  const [tpl, setTpl] = useState({ name:"Safe Work Instruction", category:"Safe Work Instruction", description:"Company Safe Work Instruction template.", document_prefix:"SWI" });
  const [wsId, setWsId] = useState(null);
  const [busy, setBusy] = useState(false);

  const next = () => setStep((s) => Math.min(5, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const createWs = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", ws);
      setWsId(data.id);
      await refresh();
      localStorage.setItem("fx_ws", data.id);
      next();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create workspace"); }
    finally { setBusy(false); }
  };

  const createTpl = async () => {
    if (!wsId) return next();
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/templates`, tpl);
      next();
    } catch (e) { toast.error("Could not create template"); }
    finally { setBusy(false); }
  };

  const finish = async () => { await refresh(); nav("/app"); };

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/5 py-4 px-5"><FexidocLogo /></header>
      <div className="max-w-2xl mx-auto px-5 py-12">
        <div className="mono text-xs uppercase tracking-widest text-cyan-300 mb-2">Step {step} of 5</div>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-8"><div className="h-full fx-btn-primary" style={{width:`${(step/5)*100}%`}}/></div>

        {step === 1 && (
          <div className="fx-panel p-8" data-testid="onboarding-step-1">
            <h1 className="font-display text-2xl font-bold">Welcome to Fexidoc</h1>
            <p className="text-slate-400 mt-1 text-sm">Let's teach Fexidoc how your organisation documents work.</p>
            <div className="mt-6 space-y-4">
              <Text label="Workspace name" value={ws.name} onChange={(v)=>setWs({...ws, name:v, company_name: ws.company_name || v})} testid="ob-workspace-name"/>
              <Select label="Industry" value={ws.industry} options={industries} onChange={(v)=>setWs({...ws, industry:v})} testid="ob-industry"/>
            </div>
            <div className="mt-8 flex justify-end"><Primary onClick={next} disabled={!ws.name.trim()} testid="ob-next-1">Continue <ArrowRight size={14}/></Primary></div>
          </div>
        )}

        {step === 2 && (
          <div className="fx-panel p-8" data-testid="onboarding-step-2">
            <h1 className="font-display text-2xl font-bold">Company identity</h1>
            <p className="text-slate-400 mt-1 text-sm">This appears on your generated documents.</p>
            <div className="mt-6 space-y-4">
              <Text label="Company name" value={ws.company_name} onChange={(v)=>setWs({...ws, company_name:v})} testid="ob-company-name"/>
              <div className="grid grid-cols-2 gap-4">
                <Colour label="Primary brand colour" value={ws.primary_colour} onChange={(v)=>setWs({...ws, primary_colour:v})} testid="ob-colour-primary"/>
                <Colour label="Secondary brand colour" value={ws.secondary_colour} onChange={(v)=>setWs({...ws, secondary_colour:v})} testid="ob-colour-secondary"/>
              </div>
            </div>
            <Nav onBack={back} onNext={createWs} nextLabel={busy?"Creating…":"Create workspace"} disabled={busy} nextTestid="ob-next-2"/>
          </div>
        )}

        {step === 3 && (
          <div className="fx-panel p-8" data-testid="onboarding-step-3">
            <h1 className="font-display text-2xl font-bold">Upload standards</h1>
            <p className="text-slate-400 mt-1 text-sm">You can upload brand guidelines, formatting guides and example documents later from Standards. Skip for now if you like.</p>
            <div className="mt-6 border-2 border-dashed border-[#1E293B] rounded-xl p-10 text-center text-slate-500 text-sm">You'll upload standards from the Standards page after onboarding.</div>
            <Nav onBack={back} onNext={next} nextLabel="Continue" nextTestid="ob-next-3"/>
          </div>
        )}

        {step === 4 && (
          <div className="fx-panel p-8" data-testid="onboarding-step-4">
            <h1 className="font-display text-2xl font-bold">Add your first template</h1>
            <p className="text-slate-400 mt-1 text-sm">Choose a template type. You can add more later.</p>
            <div className="mt-6 space-y-4">
              <Text label="Template name" value={tpl.name} onChange={(v)=>setTpl({...tpl, name:v})} testid="ob-tpl-name"/>
              <Select label="Category" value={tpl.category} options={["Safe Work Instruction","Standard Operating Procedure","Maintenance Procedure","Inspection Form","Policy","Technical Report"]} onChange={(v)=>setTpl({...tpl, category:v, document_prefix: v.split(" ").map(w=>w[0]).join("").slice(0,3).toUpperCase() })} testid="ob-tpl-category"/>
              <Text label="Document number prefix" value={tpl.document_prefix} onChange={(v)=>setTpl({...tpl, document_prefix:v})} testid="ob-tpl-prefix"/>
              <Text label="Description" value={tpl.description} onChange={(v)=>setTpl({...tpl, description:v})} testid="ob-tpl-desc"/>
            </div>
            <Nav onBack={back} onNext={createTpl} nextLabel={busy?"Adding…":"Add template"} disabled={busy} nextTestid="ob-next-4"/>
          </div>
        )}

        {step === 5 && (
          <div className="fx-panel p-8 text-center" data-testid="onboarding-step-5">
            <CheckCircle2 size={40} className="mx-auto text-cyan-400"/>
            <h1 className="font-display text-2xl font-bold mt-4">Your workspace is ready</h1>
            <p className="text-slate-400 mt-2 text-sm">Create your first company-standard document in minutes.</p>
            <button onClick={finish} className="fx-btn-primary text-white font-medium px-5 py-2.5 rounded-lg mt-6" data-testid="ob-finish">Create your first document</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Text({label, value, onChange, testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><input value={value} onChange={(e)=>onChange(e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/></label>);
}
function Select({label, value, options, onChange, testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><select value={value} onChange={(e)=>onChange(e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}>{options.map(o => <option key={o} value={o}>{o}</option>)}</select></label>);
}
function Colour({label, value, onChange, testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><div className="flex items-center gap-2"><input type="color" value={value} onChange={(e)=>onChange(e.target.value)} className="w-10 h-10 rounded-md bg-transparent border border-[#1E293B]" data-testid={testid}/><input value={value} onChange={(e)=>onChange(e.target.value)} className="flex-1 bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 mono focus:border-cyan-400 outline-none"/></div></label>);
}
function Primary({children, onClick, disabled, testid}) {
  return <button onClick={onClick} disabled={disabled} className="fx-btn-primary text-white font-medium px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid={testid}>{children}</button>;
}
function Nav({onBack, onNext, nextLabel, disabled, nextTestid}) {
  return (<div className="mt-8 flex justify-between"><button onClick={onBack} className="text-slate-400 hover:text-white px-3 py-2 rounded-lg inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button><Primary onClick={onNext} disabled={disabled} testid={nextTestid}>{nextLabel} <ArrowRight size={14}/></Primary></div>);
}
